import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { safeNext } from "@/lib/return-to";
import { useEffect, useState } from "react";
import { usePublishPageLanguage } from "@/lib/page-language";
import { useServerFn } from "@tanstack/react-start";
import { AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { amberPill } from "@/components/cabinet/CabinetCards";
import { setAccountLocale } from "@/lib/locale.functions";
import { isAccountLocale, type AccountLocale } from "@/lib/locale";
import { AUTH_ERR, authErrorText, readAuthError } from "@/lib/auth-errors";

// Magic-link / OTP callback. URL path is /auth/callback (the trailing
// underscore on the file keeps it OUT of the /auth admin-login layout).
// Handles both flows defensively:
//   * PKCE  → ?code=... → exchangeCodeForSession()
//   * implicit (default) → tokens in the URL hash → detectSessionInUrl
//     parses them on client init; we react via onAuthStateChange + getSession.
export const Route = createFileRoute("/auth_/callback")({
  head: () => ({
    meta: [{ title: "Yolutap" }, { name: "robots", content: "noindex" }],
    // Brand fonts — this route renders standalone, outside the cabinet shell.
    links: [
    ],
  }),
  component: AuthCallback,
});

// The short machine string that goes under the sentence. Support cannot act on
// "could not sign in" alone, and the person cannot read Supabase's English
// error_description — so the reader gets our sentence and their screenshot
// carries the code. Never invented: when nothing names the failure, the second
// line is simply absent.
function detailOf(e: unknown): string | null {
  if (typeof e !== "object" || e === null) return null;
  const o = e as { code?: unknown; name?: unknown };
  if (typeof o.code === "string" && o.code) return o.code;
  if (typeof o.name === "string" && o.name) return o.name;
  return null;
}

function AuthCallback() {
  const navigate = useNavigate();
  const captureLocale = useServerFn(setAccountLocale);
  const [error, setError] = useState<string | null>(null);
  // The raw failure code shown under the message. Null → no second line.
  const [detail, setDetail] = useState<string | null>(null);
  const [lang, setLang] = useState<AccountLocale>("ru");
  const t = AUTH_ERR[lang];
  usePublishPageLanguage(lang);

  useEffect(() => {
    let done = false;
    // The language of entry, carried here from the login card (which got it
    // from the marketing page). This is the ONE moment where the site language
    // and an authenticated account exist at the same time — miss it and the
    // account keeps the column default forever, which is exactly how an EN
    // visitor ended up with a Russian cabinet. Write-once, guarded server-side
    // on profiles.locale_set_at; never blocks the redirect.
    const captureFrom = (u: URL) => {
      const lang = u.searchParams.get("lang");
      if (!isAccountLocale(lang)) return;
      void captureLocale({ data: { locale: lang } }).catch(() => undefined);
    };
    const goCabinet = (session: unknown) => {
      if (session && !done) {
        done = true;
        const here = new URL(window.location.href);
        captureFrom(here);
        // Вернуть туда, откуда пришёл на вход (src/lib/return-to.ts); иначе — кабинет.
        const next = safeNext(here.searchParams.get("next"));
        if (next) window.location.replace(next);
        else navigate({ to: "/cabinet", replace: true });
      }
    };

    const url = new URL(window.location.href);
    const entryLang = url.searchParams.get("lang");
    const l: AccountLocale = isAccountLocale(entryLang) ? entryLang : "ru";
    setLang(l);

    // Provider errors arrive in the query or the hash. We translate the CODE —
    // error_description is unlocalised English and reads like a stack trace, so
    // it never reaches the screen whole; the code alone goes under the message.
    const { code, error: errParam } = readAuthError(window.location.href);
    if (code || errParam) {
      setError(authErrorText(l, code, errParam));
      setDetail(code ?? errParam);
      return;
    }

    // Implicit flow: the client sets the session from the hash asynchronously.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => goCabinet(s));

    (async () => {
      try {
        const codeParam = url.searchParams.get("code");
        if (codeParam) {
          // PKCE flow, if the project is configured for it.
          const { error } = await supabase.auth.exchangeCodeForSession(codeParam);
          if (error) throw error;
        }
        const { data } = await supabase.auth.getSession();
        goCabinet(data.session);

        // Fallback: give implicit-flow detection a moment, then decide.
        window.setTimeout(async () => {
          if (done) return;
          const { data } = await supabase.auth.getSession();
          if (data.session) goCabinet(data.session);
          // Not a browser mismatch — the verifier is stored per ORIGIN, so the
          // usual cause is requesting the link on one address (a dev server)
          // and opening it on another (prod, per Supabase's Site URL). Nothing
          // names it beyond that, so there is no code to show.
          else setError(AUTH_ERR[l].no_session);
        }, 2500);
      } catch (e) {
        setError(AUTH_ERR[l].generic);
        setDetail(detailOf(e));
      }
    })();

    return () => sub.subscription.unsubscribe();
    // captureLocale is intentionally out of the dependency list: re-running
    // this effect would re-subscribe the auth listener and re-arm the 2.5s
    // fallback timer mid-login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-yt-cream p-6 font-yt-sans text-yt-ink">
      <div className="w-full max-w-md">
        {error ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
                <div className="min-w-0">
                  <p className="text-base leading-snug text-destructive">{error}</p>
                  {/* The code, for a support screenshot. Untranslated on
                      purpose — it is the machine's word, not ours — and
                      omitted entirely when there is nothing to name. */}
                  {detail && (
                    <p className="mt-1.5 text-xs leading-snug break-all text-yt-ink/45">{detail}</p>
                  )}
                </div>
              </div>
            </div>
            {/* Single action on the screen → amber, dark text. Plain anchor,
                not <Link>: the checked-in routeTree.gen.ts predates /cabinet,
                so a typed link to it doesn't compile — and a failed sign-in is
                the right moment for a clean full reload anyway. The entry
                language rides along so the retry doesn't drop into Russian. */}
            <a
              href={`/cabinet?lang=${lang}`}
              className={`${amberPill} flex min-h-[44px] w-full items-center justify-center text-base!`}
            >
              {t.cta}
            </a>
          </div>
        ) : (
          <p className="text-center text-base text-yt-ink/60">…</p>
        )}
      </div>
    </div>
  );
}
